// src/services/documentCheckinService.js
import { DEFAULT_URL } from "./ApiConfig";

/**
 * Service for handling document check-in configurations and submissions
 */
const documentCheckinService = {
  /**
   * Get document checkin configuration by slug
   *
   * @param {string} slug - Configuration slug (e.g., "proof-of-residency")
   * @returns {Promise<Object>} - Configuration object
   */
  getConfigBySlug: async (slug, firebaseToken) => {
    // return {
    //     "id": "DCCO_SElrzF3Zek",
    //     "updatedBy": "user-123",
    //     "updateDate": "2025-09-13T05:46:35.414+00:00",
    //     "name": "Proof of Address Verification",
    //     "version": "1.0.0",
    //     "description": "Verify customer residential address using utility bills or bank statements",
    //     "slug": "proof-of-address-v1",
    //     "configurationJson": "{\"schemaVersion\":\"1.0.0\",\"meta\":{\"slug\":\"proof-of-address-v1\",\"displayName\":\"Proof of Address\",\"purpose\":\"Verify residential address for KYC compliance\",\"owner\":\"insurer-abc\",\"effectiveFrom\":\"2025-09-15\",\"effectiveTo\":null},\"files\":{\"options\":[{\"types\":[\"pdf\"],\"maxFiles\":1,\"maxSizeMB\":10.0,\"description\":\"One PDF document (utility bill, bank statement, etc.)\"},{\"types\":[\"jpg\",\"jpeg\",\"png\",\"heic\"],\"maxFiles\":3,\"maxSizeMB\":5.0,\"description\":\"Up to 3 photos of your document\"}]},\"ui\":{\"locale\":\"en-US\",\"instructions\":[\"Upload a recent utility bill, bank statement, or lease agreement.\",\"Ensure your full name and address are clearly visible.\",\"Document must be issued within the last 90 days.\"]},\"fields\":[{\"id\":\"full_name\",\"title\":\"Full Name\",\"type\":\"person_name\",\"required\":true,\"source\":\"extracted\",\"validation\":{\"mustMatchProfileField\":\"user.name\",\"onFail\":\"Name on document must match your profile name.\",\"geoResolve\":null,\"recentWithinDays\":null}},{\"id\":\"address\",\"title\":\"Residential Address\",\"type\":\"us_address\",\"required\":true,\"source\":\"extracted\",\"validation\":{\"mustMatchProfileField\":\"user.address\",\"onFail\":\"Address on document doesn't match your profile address.\",\"geoResolve\":true,\"recentWithinDays\":null}},{\"id\":\"document_date\",\"title\":\"Document Date\",\"type\":\"document_date\",\"required\":true,\"source\":\"extracted\",\"validation\":{\"mustMatchProfileField\":null,\"onFail\":\"Document must be issued within the last 90 days.\",\"geoResolve\":null,\"recentWithinDays\":90}},{\"id\":\"document_type\",\"title\":\"Document Type\",\"type\":\"document_type\",\"required\":true,\"source\":\"extracted\",\"validation\":null},{\"id\":\"contact_email\",\"title\":\"Contact Email (Optional)\",\"type\":\"email\",\"required\":false,\"source\":\"user_entered\",\"validation\":null}]}",
    //     "contributorId": "user-123",
    //     "status": "DRAFT",
    //     "archived": false
    // };
    try {
      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/document-checkin/config/by-slug/${slug}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${firebaseToken}`,
            "x-user-token": firebaseToken,
          },
          headers2: {
            "Content-Type": "application/json",
            PartnerId: "DBUY_z5tZ4iDrLBhh",
            ClientId: "UpXlgPNwc55mae3tvrrL",
            ClientSecret:
              "544lNc82eryR9LeGPkrjg0Y8kmTh3nNekBbjWiItJje8i54DmpWV5gDdGxOhNgu9",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Configuration not found for slug: ${slug}`);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch configuration: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching config by slug:", error);
      throw error;
    }
  },

  /**
   * Get document checkin configuration by ID
   *
   * @param {string} configId - Configuration ID
   * @returns {Promise<Object>} - Configuration object
   */
  getConfigById: async (configId) => {
    try {
      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/document-checkin/config/${configId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Configuration not found for ID: ${configId}`);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch configuration: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching config by ID:", error);
      throw error;
    }
  },

  /**
   * Upload a file using the existing file upload service
   * Reuses the same upload method from insurancePolicyService
   *
   * @param {File} file - The file to upload
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<string>} - URL of the uploaded file
   */
  uploadFile: async (file, firebaseToken) => {
    try {
      let deviceInfo = {};

      // Convert file to base64
      const base64File = await fileToBase64(file);

      // Prepare payload for file upload (same as insurancePolicyService)
      const payload = {
        name: file.name,
        deviceManufacturer: deviceInfo.manufacturer || "Web",
        deviceModel: deviceInfo.model || "Browser",
        deviceOS: deviceInfo.os || navigator.userAgent,
        deviceIdentifier:
          deviceInfo.identifier ||
          (crypto.randomUUID
            ? crypto.randomUUID()
            : "web_" +
              Date.now() +
              "_" +
              Math.random().toString(36).substring(2, 15)),
        deviceType: deviceInfo.type || "Web",
        latitude: deviceInfo.latitude || 0,
        longitude: deviceInfo.longitude || 0,
        file: base64File,
      };

      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(`${API_URL}/api/file/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "File upload failed");
      }

      const data = await response.json();
      return data.fileUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  },

  /**
   * Submit document checkin with user inputs and document URLs
   *
   * @param {string} configId - Configuration ID
   * @param {string} customerId - Customer/User ID
   * @param {Array} userInputs - Array of user input objects {fieldName, fieldValue}
   * @param {Array} documentUrls - Array of document URL objects
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - Submission response
   */
  submitCheckin: async (
    configId,
    customerId,
    userInputs,
    documentUrls,
    firebaseToken
  ) => {
    try {
      const payload = {
        configId,
        customerId,
        userInputs: userInputs || [],
        documentUrls: documentUrls || [],
      };

      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(`${API_URL}/api/document-checkin/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || "Document checkin submission failed"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting checkin:", error);
      throw error;
    }
  },

  /**
   * Process complete document checkin workflow: upload files and submit checkin
   *
   * @param {string} configId - Configuration ID
   * @param {string} customerId - Customer/User ID
   * @param {Array} userInputs - User form data
   * @param {Array} files - Files to upload
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - Complete workflow result
   */
  processDocumentCheckin: async (
    configId,
    customerId,
    userInputs,
    files,
    firebaseToken
  ) => {
    try {
      console.log("Processing document checkin:", {
        configId,
        customerId,
        userInputs,
        files: files?.length,
      });

      // Step 1: Upload all files and get URLs (or use existing URLs)
      const documentUrls = [];

      if (files && files.length > 0) {
        for (const file of files) {
          try {
            let fileUrl;
            
            // If file already has a URL, use it (it was uploaded by the UI component)
            if (file.url) {
              fileUrl = file.url;
            } 
            // Otherwise if it's a raw File object, upload it
            else if (file instanceof File || (file.name && file.size && !file.url)) {
                 // Note: uploadFile expects a File object (Blob), so if 'file' is just metadata sans-url, 
                 // this will fail. But we assume if no url, it might be a File.
                 // If it is just metadata, we can't upload it anyway.
                 fileUrl = await documentCheckinService.uploadFile(
                    file,
                    firebaseToken
                 );
            } else {
                 console.warn("Skipping invalid file object:", file);
                 continue;
            }

            documentUrls.push({
              documentUrl: fileUrl,
              originalFileName: file.name || file.originalFileName,
              contentType: file.type || file.contentType,
              fileSizeBytes: file.size || file.fileSizeBytes,
              userNote: null,
            });
          } catch (uploadError) {
            console.error("Failed to process file:", file.name, uploadError);
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }

      // Step 2: Submit the checkin with user inputs and document URLs
      const result = await documentCheckinService.submitCheckin(
        configId,
        customerId,
        userInputs,
        documentUrls,
        firebaseToken
      );

      return {
        success: true,
        result,
        documentUrls,
        message: "Document checkin submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to process document checkin",
      };
    }
  },

  /**
   * Get checkin status by ID
   *
   * @param {string} checkinId - Checkin ID
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - Checkin status and details
   */
  getCheckinStatus: async (checkinId, firebaseToken) => {
    try {
      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/document-checkin/${checkinId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Checkin not found: ${checkinId}`);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch checkin status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching checkin status:", error);
      throw error;
    }
  },

  /**
   * Get checkin status by ID
   *
   * @param {string} checkinId - Checkin ID
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - Checkin status and details
   */
  getCheckins: async (firebaseToken) => {
    try {
      const uri = process.env.REACT_APP_API_BASE_URL || DEFAULT_URL;
      const API_URL = uri || "https://api.ownli.co";

      const response = await fetch(
        `${API_URL}/api/document-checkin/checkins/alarm-monitoring-verification-v1`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Checkin not found`);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch checkin status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching checkin status:", error);
      throw error;
    }
  },
};

/**
 * Helper function to convert a file to base64
 * (Same as insurancePolicyService implementation)
 *
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 representation of the file
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export default documentCheckinService;
